import { createContext, useContext, PropsWithChildren, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { MonitorAlertEventDto, MonitorAlertFullDto } from '@/app/Alert/types';
import { formatInTimeZone } from 'date-fns-tz';
import { sortEventsByDateAscending } from '../utils';
import { useContextualTimeZone } from '@/components/TimezoneProvider';
import { deriveTaggedEventIds } from '@/lib/tagging';
import { preloadImage, useImagePreloader } from '@/lib/image';
import { Telemetry } from '@/lib/telemetry';

interface AdminAlertContextValue {
    alert: MonitorAlertFullDto | undefined;
    selectedEvent: MonitorAlertEventDto | null;
    setSelectedEvent: (event: MonitorAlertEventDto) => void;
    sortedEventsForNavigation: MonitorAlertEventDto[];
    goToPreviousEvent: () => void;
    goToNextEvent: () => void;
    currentEventInfo: {
        current: number;
        total: number;
    };
    groupedEvents: Record<string, MonitorAlertEventDto[]>;
    eventsContainerRef: React.RefObject<HTMLDivElement>;
    eventRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
    getEventThumbnail: (event: MonitorAlertEventDto) => string | undefined;
    setSelectedEventImage: (image: string | null) => void;
    eventsData: MonitorAlertEventDto[];
    setViewIndex: (index: number | null) => void;
    taggedEventIds: Set<string>;
    tagEvent: (eventId: string) => void;
    showTagDialog: boolean;
    setShowTagDialog: (show: boolean) => void;
    startTimeOverrideMs: number | null;
    setStartTimeOverrideMs: (ms: number | null) => void;
    /** Shared image cache: eventId -> url (set once preloaded) */
    imageCache: Record<string, string>;
    /** Loading status per event: missing key = no image URL for that event */
    imageCacheStatus: Record<string, 'loading' | 'loaded' | 'error'>;
}

const AdminAlertContext = createContext<AdminAlertContextValue | undefined>(undefined);

interface AdminAlertProviderProps extends PropsWithChildren {
    alert: MonitorAlertFullDto | undefined;
}

export function AdminAlertProvider({
    children,
    alert,
}: AdminAlertProviderProps) {
    const [selectedEvent, setSelectedEvent] = useState<MonitorAlertEventDto | null>(null);
    const [viewIndex, setViewIndex] = useState<number | null>(null);
    const [selectedEventImage, setSelectedEventImage] = useState<string | null>(null);
    
    // Derive tagged event IDs from the alert's recentNotes
    const taggedEventIds = useMemo(() => deriveTaggedEventIds(alert?.recentNotes), [alert?.recentNotes]);
    
    // Tag dialog state (shared between event list and image viewer)
    const [showTagDialog, setShowTagDialog] = useState(false);

    // Start time override - optimistic local state merged with server-derived value
    // undefined = not set locally (use server), null = explicitly cleared, number = explicitly set
    const [localStartTimeOverrideMs, setLocalStartTimeOverrideMs] = useState<number | null | undefined>(undefined);

    const serverStartTimeOverrideMs = useMemo(() => {
      if (!alert?.recentNotes) return null;
      const sorted = [...alert.recentNotes].sort(
        (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
      );
      for (const note of sorted) {
        const text = note.note || '';
        if (text.startsWith('[START_TIME_OVERRIDE]')) {
          const iso = text.replace('[START_TIME_OVERRIDE]', '').trim();
          if (iso === 'NONE') return null;
          const ms = new Date(iso).getTime();
          return isNaN(ms) ? null : ms;
        }
      }
      return null;
    }, [alert?.recentNotes]);

    const startTimeOverrideMs = localStartTimeOverrideMs !== undefined ? localStartTimeOverrideMs : serverStartTimeOverrideMs;
    const setStartTimeOverrideMs = useCallback((ms: number | null) => {
      setLocalStartTimeOverrideMs(ms);
    }, []);

    // Reset local override once server data reflects the same value
    useEffect(() => {
      if (localStartTimeOverrideMs !== undefined && localStartTimeOverrideMs === serverStartTimeOverrideMs) {
        setLocalStartTimeOverrideMs(undefined);
      }
    }, [serverStartTimeOverrideMs, localStartTimeOverrideMs]);

    // tagEvent is now a no-op since tagged state is derived from server data
    // The query invalidation in AdminAlertImageViewer will refresh the data
    const tagEvent = useCallback((_eventId: string) => {
        // No-op - tagged state is derived from alert.recentNotes
    }, []);
    // Refs for auto-scrolling functionality
    const eventsContainerRef = useRef<HTMLDivElement>(null);
    const eventRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const { timeZone } = useContextualTimeZone();
    
  const eventsData = alert?.events || [];

      // Group events by date function - memoized
  const groupEventsByDate = useCallback((events: MonitorAlertEventDto[]) => {
    const groups: Record<string, MonitorAlertEventDto[]> = {};
    events.forEach((event) => {
      const dateKey = event.occurred ? formatInTimeZone(new Date(event.occurred), timeZone, 'yyyy-MM-dd') : 'Unknown';
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });

    // Sort events within each date group chronologically (oldest first)
    Object.keys(groups).forEach((dateKey) => {
      groups[dateKey] = sortEventsByDateAscending(groups[dateKey]) as MonitorAlertEventDto[];
    });

    return groups;
  }, []);

  // Memoize grouped events to prevent recalculation
  const groupedEvents = useMemo(
    () => groupEventsByDate(eventsData),
    [eventsData, groupEventsByDate]
  );

  // Create a flattened array of events in display order for navigation
  const sortedEventsForNavigation = useMemo(() => {
    const sortedEvents: MonitorAlertEventDto[] = [];
    // Get date keys and sort them chronologically (oldest first)
    const dateKeys = Object.keys(groupedEvents).sort((a, b) => {
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return new Date(a).getTime() - new Date(b).getTime();
    });

    // Add events from each date group in order
    dateKeys.forEach((dateKey) => {
      sortedEvents.push(...groupedEvents[dateKey]);
    });

    return sortedEvents;
  }, [groupedEvents]);

  // Helper function to get the first image from an event
  const getEventThumbnail = useCallback((event: MonitorAlertEventDto): string | undefined => {
    const imageValue = event.values?.find((v) => v.type === 'SnapshotImage' && v.sourceUrl);
    return imageValue?.sourceUrl;
  }, []);

  // Shared image preloading - single queue for both viewer and thumbnails
  const [imageCache, setImageCache] = useState<Record<string, string>>({});
  const [imageCacheStatus, setImageCacheStatus] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});

  const preloadEntries = useMemo(() => {
    if (!alert?.events) return [];

    const entries = alert.events
      .map((event, idx) => ({
        id: event.id as string,
        url: event.values?.find((v) => v.type === 'SnapshotImage' && v.sourceUrl)?.sourceUrl as string | undefined,
        priority: -idx,
      }))
      .filter((e): e is { id: string; url: string; priority: number } => e.url != undefined);

    // Mark all entries as loading up-front
    setImageCacheStatus((prev) => {
      const next = { ...prev };
      for (const e of entries) {
        if (!next[e.id]) next[e.id] = 'loading';
      }
      return next;
    });

    return entries;
  }, [alert]);

  useImagePreloader(preloadEntries, {
    onLoad: (entry) => {
      setImageCache((prev) => ({ ...prev, [entry.id]: entry.url }));
      setImageCacheStatus((prev) => ({ ...prev, [entry.id]: 'loaded' }));
    },
    onError: (entry) => {
      Telemetry.captureImageLoadError(entry.url, { eventId: entry.id, source: 'preloader' });
      setImageCacheStatus((prev) => ({ ...prev, [entry.id]: 'error' }));
    },
  });

  // Eagerly load the selected event's image if it's not cached yet
  useEffect(() => {
    if (!selectedEvent?.id || imageCache[selectedEvent.id]) return;
    const url = getEventThumbnail(selectedEvent);
    if (!url) return;
    preloadImage(url).then(
      () => setImageCache((prev) => ({ ...prev, [selectedEvent.id!]: url })),
      () => {}, // queue will handle it
    );
  }, [selectedEvent?.id, imageCache, getEventThumbnail]);

  // Navigation functions with reduced dependencies and auto-scrolling
  const goToPreviousEvent = useCallback(() => {
    if (viewIndex !== null && sortedEventsForNavigation.length > 0) {
      // Loop around: if at first event, go to last event
      const newIndex = viewIndex === 0 ? sortedEventsForNavigation.length - 1 : viewIndex - 1;
      const event = sortedEventsForNavigation[newIndex];
      if (event) {
        setSelectedEvent(event);
        setSelectedEventImage(getEventThumbnail(event) || null);
        setViewIndex(newIndex);

        // Auto-scroll to keep selected event in view
        setTimeout(() => {
          const eventElement = event.id ? eventRefs.current.get(event.id) : null;
          if (eventElement && eventsContainerRef.current) {
            const container = eventsContainerRef.current;
            const elementRect = eventElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            
            // Only scroll if element is outside visible area
            if (elementRect.top < containerRect.top + 60 || elementRect.bottom > containerRect.bottom - 60) {
              eventElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }
        }, 50);
      }
    }
  }, [viewIndex, sortedEventsForNavigation, getEventThumbnail]);

  const goToNextEvent = useCallback(() => {
    if (viewIndex !== null && sortedEventsForNavigation.length > 0) {
      // Loop around: if at last event, go to first event
      const newIndex = viewIndex === sortedEventsForNavigation.length - 1 ? 0 : viewIndex + 1;
      const event = sortedEventsForNavigation[newIndex];
      if (event) {
        setSelectedEvent(event);
        setSelectedEventImage(getEventThumbnail(event) || null);
        setViewIndex(newIndex);

        // Auto-scroll to keep selected event in view
        setTimeout(() => {
          const eventElement = event.id ? eventRefs.current.get(event.id) : null;
          if (eventElement && eventsContainerRef.current) {
            const container = eventsContainerRef.current;
            const elementRect = eventElement.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            // Only scroll if element is outside visible area (with padding for sticky header)
            if (elementRect.top < containerRect.top + 60 || elementRect.bottom > containerRect.bottom - 60) {
              eventElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }
          }
        }, 50);
      }
    }
  }, [viewIndex, sortedEventsForNavigation, getEventThumbnail]);

  // Get current event position info - memoized
  const currentEventInfo = useMemo(() => {
    if (viewIndex === null || !sortedEventsForNavigation.length) {
      return { current: 0, total: 0 };
    }
    return { current: viewIndex + 1, total: sortedEventsForNavigation.length };
  }, [viewIndex, sortedEventsForNavigation.length]);

  // Load first event by default
  useEffect(() => {
    if (sortedEventsForNavigation.length > 0 && !selectedEvent) {
      const firstEvent = sortedEventsForNavigation[0];
      setSelectedEvent(firstEvent);
      setSelectedEventImage(getEventThumbnail(firstEvent) || null);
      setViewIndex(0);
    }
  }, [sortedEventsForNavigation, selectedEvent, getEventThumbnail]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPreviousEvent();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNextEvent();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPreviousEvent, goToNextEvent]);

    const value: AdminAlertContextValue = {
        alert,
        selectedEvent,
        setSelectedEvent,
        sortedEventsForNavigation,
        goToPreviousEvent,
        goToNextEvent,
        currentEventInfo,
        groupedEvents,
        eventsContainerRef,
        eventRefs,
        getEventThumbnail,
        setSelectedEventImage,
        eventsData,
        setViewIndex,
        taggedEventIds,
        tagEvent,
        showTagDialog,
        setShowTagDialog,
        startTimeOverrideMs,
        setStartTimeOverrideMs,
        imageCache,
        imageCacheStatus,
    };

    return <AdminAlertContext.Provider value={value}>{children}</AdminAlertContext.Provider>;
}


export function useAdminAlertContext() {
  const context = useContext(AdminAlertContext);
  if (!context) {
    throw new Error('useAdminAlertContext must be used within AdminAlertProvider');
  }
  return context;
}
